import { FormEvent, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { getAllRetailers } from "../../../apis/retailers";
import {
  createGroup,
  deleteGroup,
  getAllGroups,
  updateGroup,
} from "../../../apis/groups";
import { errorAlert, successAlert } from "../../../style/scss/utils/Alerts";
import { formatDate } from "../../../environment";

type Retailer = { id: number; company_name?: string; full_name?: string };
type Group = {
  id: number;
  groupName: string;
  groupCode: string;
  groupHeadName: string;
  branchId?: number;
  retailer?: Retailer;
  customers?: Customer[];
  createdAt?: string;
};
type Customer = {
  id: number;
  full_name?: string;
  firstName?: string;
  lastName?: string;
  email?: string;
  phoneNumber?: string;
  phone_number?: string;
  loanAccountNumber?: string;
};

const emptyGroup = { groupName: "", groupCode: "", groupHeadName: "", branchId: "" };

const GroupList = () => {
  const [groups, setGroups] = useState<Group[]>([]);
  const [retailers, setRetailers] = useState<Retailer[]>([]);
  const [form, setForm] = useState(emptyGroup);
  const [editingGroup, setEditingGroup] = useState<Group | null>(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [query, setQuery] = useState("");

  const schemaName = sessionStorage.getItem("schemaName") || undefined;
  const user = JSON.parse(sessionStorage.getItem("info") || "{}");
  const requestData = { ...(schemaName ? { schemaName } : {}), user_id: user.id, vendor_id: user.vendor_id, user_type: user.type };

  const loadGroups = async () => {
    setLoading(true);
    try {
      const response = await getAllGroups(requestData);
      setGroups(response?.data || []);
    } catch (error) {
      errorAlert("Unable to load groups", "Please try again.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadGroups();
    if (!user.id) {
      setRetailers([]);
      return;
    }

    getAllRetailers({ user_id: user.id, vendor_id: user.vendor_id, user_type: user.type })
      .then((response) => setRetailers(response?.data || []))
      .catch(() => setRetailers([]));
  }, []);

  const resetForm = () => {
    setForm(emptyGroup);
    setEditingGroup(null);
  };

  const submitGroup = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setSaving(true);
    const payload = {
      ...requestData,
      ...form,
      branchId: Number(form.branchId),
    };

    try {
      if (editingGroup) {
        await updateGroup(editingGroup.id, payload);
        successAlert("Success", "Group updated successfully.");
      } else {
        await createGroup(payload);
        successAlert("Success", "Group created successfully.");
      }
      resetForm();
      await loadGroups();
    } catch (error: any) {
      errorAlert("Unable to save group", error?.response?.data?.message || "Please check the entered details.");
    } finally {
      setSaving(false);
    }
  };

  const editGroup = (group: Group) => {
    setEditingGroup(group);
    setForm({
      groupName: group.groupName || "",
      groupCode: group.groupCode || "",
      groupHeadName: group.groupHeadName || "",
      branchId: String(group.branchId || ""),
    });
    document.getElementById("group-form")?.scrollIntoView({ behavior: "smooth" });
  };

  const removeGroup = async (group: Group) => {
    if (!window.confirm(`Delete group \"${group.groupName}\"?`)) return;
    try {
      await deleteGroup(group.id, requestData);
      successAlert("Success", "Group deleted successfully.");
      if (editingGroup?.id === group.id) resetForm();
      await loadGroups();
    } catch (error: any) {
      errorAlert("Unable to delete group", error?.response?.data?.message || "Remove its members first, then try again.");
    }
  };

  const filteredGroups = groups.filter((group) =>
    [group.groupName, group.groupCode, group.groupHeadName]
      .filter(Boolean)
      .some((value) => value.toLowerCase().includes(query.toLowerCase()))
  );

  return (
    <div className="page-wrapper">
      <div className="content">
        <div className="d-flex justify-content-between align-items-center mb-3">
          <div>
            <h4 className="mb-1">Group Loans</h4>
            <p className="text-muted mb-0">Create groups and manage their loan members.</p>
          </div>
          <div className="d-flex gap-2">
            <Link to="/emis-records" className="btn btn-outline-primary">EMI Records</Link>
            <Link to="/customer-creations" className="btn btn-primary">Add Customer</Link>
          </div>
        </div>

        <div className="card mb-4" id="group-form">
          <div className="card-header"><h5 className="mb-0">{editingGroup ? "Edit Group" : "Create Group"}</h5></div>
          <div className="card-body">
            <form onSubmit={submitGroup} className="row g-3">
              <div className="col-md-3"><label className="form-label">Group Name</label><input required className="form-control" value={form.groupName} onChange={(event) => setForm({ ...form, groupName: event.target.value })} /></div>
              <div className="col-md-3"><label className="form-label">Group Code</label><input required className="form-control" value={form.groupCode} onChange={(event) => setForm({ ...form, groupCode: event.target.value })} /></div>
              <div className="col-md-3"><label className="form-label">Group Head Name</label><input required className="form-control" value={form.groupHeadName} onChange={(event) => setForm({ ...form, groupHeadName: event.target.value })} /></div>
              <div className="col-md-3"><label className="form-label">Retailer Branch</label><select required className="form-select" value={form.branchId} onChange={(event) => setForm({ ...form, branchId: event.target.value })}><option value="">Select retailer</option>{retailers.map((retailer) => <option key={retailer.id} value={retailer.id}>{retailer.company_name || retailer.full_name || `Retailer ${retailer.id}`}</option>)}</select></div>
              <div className="col-12 d-flex gap-2"><button type="submit" disabled={saving} className="btn btn-primary">{saving ? "Saving..." : editingGroup ? "Update Group" : "Create Group"}</button>{editingGroup && <button type="button" className="btn btn-light" onClick={resetForm}>Cancel</button>}</div>
            </form>
          </div>
        </div>

        <div className="card">
          <div className="card-header d-flex justify-content-between align-items-center"><h5 className="mb-0">All Groups</h5><input className="form-control w-auto" placeholder="Search groups" value={query} onChange={(event) => setQuery(event.target.value)} /></div>
          <div className="card-body p-0 table-responsive">
            <table className="table table-hover mb-0"><thead><tr><th>#</th><th>Created</th><th>Group</th><th>Code</th><th>Head</th><th>Branch</th><th className="text-end">Actions</th></tr></thead>
              <tbody>{loading ? <tr><td colSpan={7} className="text-center py-4">Loading groups...</td></tr> : filteredGroups.length === 0 ? <tr><td colSpan={7} className="text-center py-4">No groups found.</td></tr> : filteredGroups.map((group, index) => <tr key={group.id}><td>{index + 1}</td><td>{formatDate(group.createdAt)}</td><td>{group.groupName}</td><td>{group.groupCode}</td><td>{group.groupHeadName}</td><td>{group.retailer?.company_name || group.retailer?.full_name || group.branchId || "-"}</td><td className="text-end"><Link className="btn btn-sm btn-light me-1" to={`/group-members?group_id=${group.id}&group_name=${encodeURIComponent(group.groupName)}`}>Members</Link><button className="btn btn-sm btn-primary me-1" onClick={() => editGroup(group)}>Edit</button><button className="btn btn-sm btn-outline-danger" onClick={() => removeGroup(group)}>Delete</button></td></tr>)}</tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GroupList;
